import React, { createContext, useContext, useState, useCallback, useMemo, useEffect } from "react";

const WORKSPACES_ENABLED_KEY = "workspaces-enabled";

export interface WorkspaceTab {
  id: string;
  title: string;
  route: string;
  params?: Record<string, string>;
  history: Array<{ route: string; params?: Record<string, string>; title: string }>;
  historyIndex: number;
}

interface WorkspaceState {
  tabs: WorkspaceTab[];
  activeTabId: string | null;
  isEnabled: boolean;
}

interface NavigateOptions {
  route: string;
  params?: Record<string, string>;
  title?: string;
}

interface WorkspaceContextValue extends WorkspaceState {
  openTab: (tab?: Omit<WorkspaceTab, "id" | "history" | "historyIndex">) => string;
  closeTab: (tabId: string) => void;
  setActiveTab: (tabId: string) => void;
  toggleWorkspaces: (enabled: boolean) => void;
  closeAllTabs: () => void;
  navigateInTab: (tabId: string, options: NavigateOptions) => void;
  navigateInActiveTab: (options: NavigateOptions) => void;
  goBackInTab: (tabId: string) => boolean;
  goBackInActiveTab: () => boolean;
  goForwardInTab: (tabId: string) => boolean;
  goForwardInActiveTab: () => boolean;
}

const WorkspaceContext = createContext<WorkspaceContextValue | null>(null);

function generateTabId(): string {
  return `tab-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`;
}

// Helper to get a title from a route
function getRouteTitle(route: string): string {
  if (route === "/") return "Projects";
  if (route === "/specPilot") return "SpecPilot";
  if (route === "/comparisons") return "RevPilot";
  if (route === "/workspaces/new") return "New Tab";
  if (route === "/specPilot/new") return "New SpecPilot";
  if (route === "/revPilot") return "RevPilot";
  if (route === "/revPilot/new") return "New RevPilot";
  if (route === "/takeoffPilot") return "TakeoffPilot";
  if (route === "/takeoffPilot/new") return "New TakeoffPilot";
  if (route === "/revCollab") return "CollabPilot";
  if (route.startsWith("/revCollab/documents/")) return "CollabPilot Document";
  if (route.startsWith("/revCollab/revisions/")) return "CollabPilot Session";
  if (route === "/project") return "Project";
  if (route === "/project/documents") return "Project Documents";
  if (route.startsWith("/project/documents/")) return "Document";
  if (route.startsWith("/specPilot/") && route.endsWith("/submittal")) return "Submittal";
  if (route.startsWith("/specPilot/")) return "Job Detail";
  if (route.startsWith("/revPilot/")) return "Comparison";
  if (route.startsWith("/takeoffPilot/")) return "Takeoff Job";
  return "Tab";
}

export function WorkspaceProvider({ children }: { children: React.ReactNode }) {
  const [tabs, setTabs] = useState<WorkspaceTab[]>([]);
  const [activeTabId, setActiveTabId] = useState<string | null>(null);
  const [isEnabled, setIsEnabled] = useState(() => {
    // Initialize from localStorage
    if (typeof window !== "undefined") {
      const stored = localStorage.getItem(WORKSPACES_ENABLED_KEY);
      return stored === "true";
    }
    return false;
  });

  // Persist to localStorage when changed
  useEffect(() => {
    if (typeof window !== "undefined") {
      localStorage.setItem(WORKSPACES_ENABLED_KEY, String(isEnabled));
    }
  }, [isEnabled]);

  // When workspaces is enabled, create a default tab
  React.useEffect(() => {
    if (isEnabled && tabs.length === 0) {
      const defaultTab: WorkspaceTab = {
        id: generateTabId(),
        title: "New Tab",
        route: "/workspaces/new",
        history: [{ route: "/workspaces/new", title: "New Tab" }],
        historyIndex: 0,
      };
      setTabs([defaultTab]);
      setActiveTabId(defaultTab.id);
    }
  }, [isEnabled, tabs.length]);

  const openTab = useCallback((tab?: Omit<WorkspaceTab, "id" | "history" | "historyIndex">): string => {
    const newTab: WorkspaceTab = {
      ...(tab || { title: "New Tab", route: "/workspaces/new" }),
      id: generateTabId(),
      history: [{ route: tab?.route || "/workspaces/new", params: tab?.params, title: tab?.title || "New Tab" }],
      historyIndex: 0,
    };

    setTabs((prev) => [...prev, newTab]);
    setActiveTabId(newTab.id);

    return newTab.id;
  }, []);

  const closeTab = useCallback((tabId: string) => {
    setTabs((prev) => {
      const tabIndex = prev.findIndex((t) => t.id === tabId);
      if (tabIndex === -1) return prev;

      const newTabs = prev.filter((t) => t.id !== tabId);

      // If closing active tab, switch to another tab
      if (activeTabId === tabId && newTabs.length > 0) {
        const newIndex = Math.min(tabIndex, newTabs.length - 1);
        setActiveTabId(newTabs[newIndex].id);
      } else if (newTabs.length === 0 && isEnabled) {
        // If no tabs left and workspaces still enabled, create a new tab
        const defaultTab: WorkspaceTab = {
          id: generateTabId(),
          title: "New Tab",
          route: "/workspaces/new",
          history: [{ route: "/workspaces/new", title: "New Tab" }],
          historyIndex: 0,
        };
        newTabs.push(defaultTab);
        setActiveTabId(defaultTab.id);
      }

      return newTabs;
    });
  }, [activeTabId, isEnabled]);

  const setActiveTab = useCallback((tabId: string) => {
    setActiveTabId(tabId);
  }, []);

  const toggleWorkspaces = useCallback((enabled: boolean) => {
    setIsEnabled(enabled);
    if (!enabled) {
      // Reset when disabling
      setTabs([]);
      setActiveTabId(null);
    }
  }, []);

  const closeAllTabs = useCallback(() => {
    if (isEnabled) {
      const defaultTab: WorkspaceTab = {
        id: generateTabId(),
        title: "New Tab",
        route: "/workspaces/new",
        history: [{ route: "/workspaces/new", title: "New Tab" }],
        historyIndex: 0,
      };
      setTabs([defaultTab]);
      setActiveTabId(defaultTab.id);
    } else {
      setTabs([]);
      setActiveTabId(null);
    }
  }, [isEnabled]);

  // Navigate within a specific tab
  const navigateInTab = useCallback((tabId: string, options: NavigateOptions) => {
    const title = options.title || getRouteTitle(options.route);
    
    setTabs((prev) =>
      prev.map((tab) => {
        if (tab.id !== tabId) return tab;
        
        // Add new entry to history, removing any forward history
        const newHistory = tab.history.slice(0, tab.historyIndex + 1);
        newHistory.push({ route: options.route, params: options.params, title });
        
        return {
          ...tab,
          route: options.route,
          params: options.params,
          title,
          history: newHistory,
          historyIndex: newHistory.length - 1,
        };
      })
    );
  }, []);

  // Navigate within the active tab
  const navigateInActiveTab = useCallback((options: NavigateOptions) => {
    if (activeTabId) {
      navigateInTab(activeTabId, options);
    }
  }, [activeTabId, navigateInTab]);

  // Go back in a specific tab
  const goBackInTab = useCallback((tabId: string): boolean => {
    let wentBack = false;
    
    setTabs((prev) =>
      prev.map((tab) => {
        if (tab.id !== tabId || tab.historyIndex <= 0) return tab;
        
        wentBack = true;
        const newIndex = tab.historyIndex - 1;
        const historyEntry = tab.history[newIndex];
        
        return {
          ...tab,
          route: historyEntry.route,
          params: historyEntry.params,
          title: historyEntry.title,
          historyIndex: newIndex,
        };
      })
    );
    
    return wentBack;
  }, []);

  // Go back in the active tab
  const goBackInActiveTab = useCallback((): boolean => {
    if (!activeTabId) return false;
    return goBackInTab(activeTabId);
  }, [activeTabId, goBackInTab]);

  // Go forward in a specific tab
  const goForwardInTab = useCallback((tabId: string): boolean => {
    let wentForward = false;
    
    setTabs((prev) =>
      prev.map((tab) => {
        if (tab.id !== tabId || tab.historyIndex >= tab.history.length - 1) return tab;
        
        wentForward = true;
        const newIndex = tab.historyIndex + 1;
        const historyEntry = tab.history[newIndex];
        
        return {
          ...tab,
          route: historyEntry.route,
          params: historyEntry.params,
          title: historyEntry.title,
          historyIndex: newIndex,
        };
      })
    );
    
    return wentForward;
  }, []);

  // Go forward in the active tab
  const goForwardInActiveTab = useCallback((): boolean => {
    if (!activeTabId) return false;
    return goForwardInTab(activeTabId);
  }, [activeTabId, goForwardInTab]);

  const value = useMemo(
    () => ({
      tabs,
      activeTabId,
      isEnabled,
      openTab,
      closeTab,
      setActiveTab,
      toggleWorkspaces,
      closeAllTabs,
      navigateInTab,
      navigateInActiveTab,
      goBackInTab,
      goBackInActiveTab,
      goForwardInTab,
      goForwardInActiveTab,
    }),
    [tabs, activeTabId, isEnabled, openTab, closeTab, setActiveTab, toggleWorkspaces, closeAllTabs, navigateInTab, navigateInActiveTab, goBackInTab, goBackInActiveTab, goForwardInTab, goForwardInActiveTab]
  );

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
}

export function useWorkspace() {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error("useWorkspace must be used within a WorkspaceProvider");
  }
  return context;
}

export function useWorkspaceEnabled() {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error("useWorkspaceEnabled must be used within a WorkspaceProvider");
  }
  return context.isEnabled;
}

export function useWorkspaceNavigate() {
  const context = useContext(WorkspaceContext);
  if (!context) {
    throw new Error("useWorkspaceNavigate must be used within a WorkspaceProvider");
  }
  return context.navigateInActiveTab;
}
